import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Send, Plus, Lock, CheckCircle2, Clock, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/pages/Projects";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLE = {
  Open: "bg-amber-50 text-amber-700 border-amber-200",
  "In Progress": "bg-sky-50 text-sky-700 border-sky-200",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const StatusIcon = ({ status }) =>
  status === "Resolved" ? <CheckCircle2 size={13} /> : status === "Open" ? <Inbox size={13} /> : <Clock size={13} />;

const when = (v) => (v ? new Date(v).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");

export default function Support() {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "Question", priority: "Normal" });
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    const { data: e } = await api.get("/support/eligibility");
    setEligibility(e);
    if (!e.allowed) return;
    const { data } = await api.get("/support/tickets");
    setTickets(data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const create = async () => {
    if (form.subject.trim().length < 3) return toast.error("Give your request a subject");
    if (form.message.trim().length < 10) return toast.error("Please describe the problem in a little more detail");
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", {
        ...form, subject: form.subject.trim(), message: form.message.trim(),
      });
      toast.success(`${data.ref} raised — we have emailed you a confirmation`);
      setOpen(false);
      setForm({ subject: "", message: "", category: "Question", priority: "Normal" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (ticket) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${ticket.id}/reply`, { message: reply.trim() });
      setReply("");
      setReplyTo(null);
      toast.success("Reply sent to our support team");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (eligibility && !eligibility.allowed) {
    return (
      <Shell title="Support" subtitle="Talk to a real person about your workspace" testId="support-locked">
        <div className="panel mx-auto max-w-xl px-8 py-12 text-center">
          <Lock size={30} className="mx-auto text-slate-300" />
          <h2 className="mt-4 text-lg font-semibold text-slate-900">Support is included with every paid plan</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            {eligibility.message} You are currently on a {eligibility.trial ? "free trial" : eligibility.status} —
            choose a plan and you can raise a ticket here and get a reply by email.
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Button data-testid="support-upgrade-btn" onClick={() => navigate("/admin/billing")}>Choose a plan</Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Support" subtitle="Raise a ticket and our team will reply by email" testId="support-page"
      actions={
        <Button data-testid="support-new-btn" onClick={() => setOpen(true)} className="gap-1.5">
          <Plus size={15} /> New Ticket
        </Button>
      }>
      {tickets.length === 0 ? (
        <Empty testId="support-empty"
          text="No tickets yet. Raise one and we will pick it up — you will get a confirmation by email." />
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} data-testid={`support-ticket-${t.id}`} className="panel px-5 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t.ref}</span>
                    <span className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${STATUS_STYLE[t.status]}`}>
                      <StatusIcon status={t.status} /> {t.status}
                    </span>
                  </div>
                  <p className="mt-1 font-semibold text-slate-900">{t.subject}</p>
                  <p className="text-xs text-slate-500">
                    {t.category} · {t.priority} priority · raised {when(t.created_at)}
                  </p>
                </div>
                <Button data-testid={`support-reply-btn-${t.id}`} size="sm" variant="outline"
                  onClick={() => { setReplyTo(replyTo === t.id ? null : t.id); setReply(""); }}>
                  {replyTo === t.id ? "Cancel" : "Reply"}
                </Button>
              </div>

              <div className="mt-3 space-y-2">
                {(t.messages || []).map((m, i) => (
                  <div key={i} className={`rounded-lg border px-4 py-3 ${
                    m.side === "support" ? "border-[#1F4E79]/20 bg-[#1F4E79]/5" : "border-slate-200 bg-slate-50/60"
                  }`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      {m.side === "support" ? "Videra Support" : m.author_name} · {when(m.at)}
                    </p>
                    <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{m.body}</p>
                  </div>
                ))}
              </div>

              {replyTo === t.id && (
                <div className="mt-3">
                  <Textarea data-testid={`support-reply-input-${t.id}`} rows={3} value={reply}
                    onChange={(e) => setReply(e.target.value)} placeholder="Add to this ticket…" />
                  <div className="mt-2 flex justify-end">
                    <Button data-testid={`support-reply-send-${t.id}`} size="sm" className="gap-1.5"
                      disabled={busy || !reply.trim()} onClick={() => sendReply(t)}>
                      <Send size={14} /> Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy size={17} /> Raise a support ticket
            </DialogTitle>
            <DialogDescription>
              Our team is notified straight away and you will get a confirmation by email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Subject</label>
              <Input data-testid="support-subject-input" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Short summary of what you need" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-slate-500">Category</label>
                <Select testId="support-category-select" value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  options={eligibility?.categories || []} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Priority</label>
                <Select testId="support-priority-select" value={form.priority}
                  onChange={(v) => setForm({ ...form, priority: v })}
                  options={eligibility?.priorities || []} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">How can we help?</label>
              <Textarea data-testid="support-message-input" rows={6} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="What were you trying to do, what happened, and which project or page was it on?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="support-submit-btn" onClick={create} disabled={busy}>
              {busy ? "Sending…" : "Raise ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
